import moment, { Moment } from "moment";
import React, { useMemo } from "react";
import styled from "styled-components";

import { Spinner } from "src/shared/components";
import { withBuffer } from "src/views/volunteer/shared/utils/buffer";
import {
  blocksFromTop,
  isShiftAvailable,
  sortComputedShifts,
} from "src/views/volunteer/shift-schedule/utils";

import { Calendar } from "../calendar/Calendar";
import { CalendarFilterPill } from "../components/CalendarFilterPill";
import { EVENT_DAYS, TShiftScheduleEvent } from "../constants";
import { TIsDisabled, TIsRegistered, TShiftSchedule } from "../types";

interface CalenderViewScheduleProps {
  fetchingShifts: boolean;
  volunteerShifts: TShiftSchedule[];
  shiftFilters: string[];
  isRegistered: TIsRegistered[];
  isDisableRegistered: TIsDisabled[];
  onlyMyShifts: boolean;
  toggleOnlyMyShifts: () => void;
  onlyAvailableShifts: boolean;
  toggleOnlyAvailableShifts: () => void;
  setSelectedShift: (shiftId: number) => void;
  setViewModal: (visible: boolean) => void;
}

const convertToScheduleShift = (
  shift: TShiftSchedule,
  momentStart: Moment,
  momentEnd: Moment,
  registeredVolunteers: number,
  max: number,
  registered: TIsRegistered,
  disabled: TIsDisabled
): TShiftScheduleEvent => {
  const blocks = moment.duration(momentEnd.diff(momentStart)).asHours();

  return {
    id: shift.shift_id,
    title: shift.shift,
    category: shift.role,
    start: momentStart,
    end: momentEnd,
    displayedStart: momentStart.format("HH:mm"),
    displayedEnd: momentEnd.format("HH:mm"),
    numberOfBlocks: blocks,
    blocksFromTop: blocksFromTop(momentStart),
    overlapColumn: 0,
    registeredVolunteers: registeredVolunteers,
    maxVolunteers: max,
    isRegistered: registered,
    isDisableRegistered: disabled,
  };
};

const VolunteerShiftCalendarView: React.FC<CalenderViewScheduleProps> = ({
  fetchingShifts,
  volunteerShifts,
  shiftFilters,
  isRegistered,
  isDisableRegistered,
  onlyMyShifts,
  toggleOnlyMyShifts,
  onlyAvailableShifts,
  toggleOnlyAvailableShifts,
  setSelectedShift,
  setViewModal,
}) => {
  const handleShiftClick = (shift_id: number) => {
    setSelectedShift(shift_id);
    setViewModal(true);
  };

  const calendarShifts = useMemo(() => {
    if (fetchingShifts) return [];

    const registeredById = new Map(
      isRegistered.map((entry) => [entry.shift_id, entry])
    );
    const myRegisteredShifts = volunteerShifts.filter(
      (shift) => registeredById.get(shift.shift_id)?.registered
    );

    // Buffered ends only matter when viewing "my shifts"; the full calendar
    // keeps wall-clock ends so other volunteers' blocks stay comparable.
    const registeredEndsById = onlyMyShifts
      ? new Map(
          withBuffer(
            myRegisteredShifts.map((shift) => ({
              shiftId: shift.shift_id,
              start: shift.time_start,
              end: shift.time_end,
            }))
          ).map((shift) => [shift.shiftId, shift.effectiveEnd])
        )
      : null;

    const computedShifts: TShiftScheduleEvent[] = [];

    volunteerShifts.forEach((shift, index) => {
      if (shiftFilters.length > 0 && !shiftFilters.includes(shift.role)) {
        return;
      }

      const registered =
        registeredById.get(shift.shift_id) ?? isRegistered[index];
      if (onlyMyShifts && !registered?.registered) return;
      if (
        onlyAvailableShifts &&
        !isShiftAvailable(shift, isDisableRegistered[index])
      ) {
        return;
      }

      const momentStart = moment(shift.time_start).tz("America/New_York");
      const displayEnd =
        registeredEndsById?.get(shift.shift_id) ?? shift.time_end;
      const momentEnd = moment(displayEnd).tz("America/New_York");
      const maxCount = shift.max_volunteer_count;

      computedShifts.push(
        convertToScheduleShift(
          shift,
          momentStart,
          momentEnd,
          shift.signup_count,
          maxCount,
          registered ?? {
            registered: false,
            shouldShow: true,
            shift_id: shift.shift_id,
          },
          isDisableRegistered[index] ?? { disabled: false, reason: "" }
        )
      );
    });

    sortComputedShifts(computedShifts);
    return computedShifts;
  }, [
    fetchingShifts,
    volunteerShifts,
    shiftFilters,
    isDisableRegistered,
    isRegistered,
    onlyMyShifts,
    onlyAvailableShifts,
  ]);

  return (
    <>
      {fetchingShifts ? (
        <Spinner text="Loading shifts..." />
      ) : (
        <CalendarShell>
          <Calendar
            daysToShow={EVENT_DAYS}
            calendarShifts={calendarShifts}
            setSelectedShift={handleShiftClick}
            collapseToSingleColumn={onlyMyShifts}
            dayBarActions={
              <DayBarActions>
                <CalendarFilterPill
                  active={onlyAvailableShifts}
                  disabled={onlyMyShifts}
                  onClick={toggleOnlyAvailableShifts}
                >
                  Available to me
                </CalendarFilterPill>
                <CalendarFilterPill
                  active={onlyMyShifts}
                  disabled={onlyAvailableShifts}
                  onClick={toggleOnlyMyShifts}
                >
                  My shifts only
                </CalendarFilterPill>
              </DayBarActions>
            }
          />
        </CalendarShell>
      )}
    </>
  );
};

const CalendarShell = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 70vh;
  height: calc(100vh - 200px);

  ${({ theme }) => theme.mediaQueries.tablet`
    min-height: 80dvh;
    height: calc(100dvh - 120px);
  `}
`;

const DayBarActions = styled.div`
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;
`;

export default VolunteerShiftCalendarView;
